import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  font-family: 'Cairo', sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Logo = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  letter-spacing: -1px;
`;

const KuwaitEmblem = styled.div`
  width: 120px;
  height: 120px;
  margin: 2rem auto;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  
  &::before {
    content: '🇰🇼';
    font-size: 4rem;
  }
`;

const TestTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  margin: 2rem 0;
  
  .experimental { color: #3498db; }
  .national { color: #e74c3c; }
`;

const FeatureList = styled.div`
  max-width: 500px;
  margin: 2rem 0;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  
  &::before {
    content: '✓';
    color: #27ae60;
    font-weight: bold;
    font-size: 1.2rem;
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const FeatureText = styled.span`
  font-size: 1.1rem;
  color: #2c3e50;
  text-align: right;
  flex: 1;
`;

const StartButton = styled.button`
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  padding: 1.2rem 3rem;
  font-size: 1.3rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  min-width: 200px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(52, 152, 219, 0.4);
  }
`;

const LanguageToggle = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: white;
  border: 2px solid #e0e0e0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3498db;
    background: #f8f9fa;
  }
`;

interface Props {
  onStartTest: () => void;
  language: 'ar' | 'en';
  onLanguageToggle: () => void;
}

const AptitudeLanding: React.FC<Props> = ({ onStartTest, language, onLanguageToggle }) => {
  const content = {
    ar: {
      title: 'ülearn',
      testTitle: 'الاختبار الوطني التجريبي',
      features: [
        'أسرع طريقة لمعرفة درجتك التقريبية',
        'الاختبار عبارة عن 15 سؤال في قسم',
        'الإنجليزي و 5 أسئلة في قسم الرياضيات',
        'اختبار مجاني ومصدر آمن من 20 دقيقة'
      ],
      startButton: 'ابدأ الاختبار',
      langToggle: 'English'
    },
    en: {
      title: 'ülearn',
      testTitle: 'National Experimental Test',
      features: [
        'Fastest way to know your approximate grade',
        'Test consists of 15 questions in section',
        'English and 5 questions in Math section',
        'Free and secure test from 20 minutes'
      ],
      startButton: 'Start Test',
      langToggle: 'عربي'
    }
  };

  const currentContent = content[language];

  return (
    <Container>
      <LanguageToggle onClick={onLanguageToggle}>
        {currentContent.langToggle}
      </LanguageToggle>
      
      <Header>
        <Logo>{currentContent.title}</Logo>
        <KuwaitEmblem />
        <TestTitle>
          <span className="experimental">الاختبار</span>{' '}
          <span className="national">الوطني</span>{' '}
          التجريبي
        </TestTitle>
      </Header>

      <FeatureList>
        {currentContent.features.map((feature, index) => (
          <FeatureItem key={index}>
            <FeatureText>{feature}</FeatureText>
          </FeatureItem>
        ))}
      </FeatureList>

      <StartButton onClick={onStartTest}>
        {currentContent.startButton}
      </StartButton>
    </Container>
  );
};

export default AptitudeLanding;
